import React, { useState, useEffect } from 'react';
import client from '../api/client';
import { C, Btn, Input, Card, Toast, Spinner, initials, useNotify } from '../utils/styles';

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const DURATIONS = [15,30,45,60,90,120];
const BOOKING_TYPES = [
  { id:'one_on_one',  label:'1-on-1',      icon:'ti-user',    desc:'One host, one guest' },
  { id:'round_robin', label:'Round Robin',  icon:'ti-refresh', desc:'Rotate among team members' },
  { id:'group',       label:'Group',        icon:'ti-users',   desc:'Multiple guests, one slot' },
];
const FORM_FIELD_TYPES = [
  { id:'text',        label:'Text Input',   icon:'ti-forms'        },
  { id:'textarea',    label:'Text Area',    icon:'ti-align-left'   },
  { id:'select',      label:'Dropdown',     icon:'ti-chevron-down' },
  { id:'radio',       label:'Radio',        icon:'ti-circle-dot'   },
  { id:'checkbox',    label:'Checkbox',     icon:'ti-checkbox'     },
  { id:'multiselect', label:'Multi-select', icon:'ti-list-check'   },
];
const TIMEZONES = [
  'Pacific/Honolulu','America/Los_Angeles','America/Denver','America/Chicago',
  'America/New_York','America/Halifax','America/Sao_Paulo','Atlantic/Azores',
  'Europe/London','Europe/Paris','Europe/Helsinki','Europe/Moscow',
  'Asia/Dubai','Asia/Karachi','Asia/Kolkata','Asia/Dhaka',
  'Asia/Bangkok','Asia/Singapore','Asia/Tokyo',
  'Australia/Sydney','Pacific/Auckland',
];

// Default availability: each day has ranges array (supports multiple slots per day)
const makeDefaultAvail = () => DAYS.reduce((a, d) => ({
  ...a,
  [d]: {
    enabled: !['Saturday','Sunday'].includes(d),
    ranges: [{ start:'09:00', end:'17:00' }],
  },
}), {});

const BLANK_PAGE = {
  title:'', slug:'', description:'', duration_mins:30,
  booking_type:'one_on_one', location:'', color:'#0099FF',
  logo_url:'', host_photo_url:'', availability: makeDefaultAvail(),
  questions:[], form_fields:[], member_ids:[],
  host_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata',
};

// Migrate old format (single start/end) to new ranges format
const migrateAvail = (avail) => {
  if (!avail) return makeDefaultAvail();
  const out = {};
  for (const day of DAYS) {
    const d = avail[day];
    if (!d) { out[day] = { enabled: false, ranges: [{ start:'09:00', end:'17:00' }] }; continue; }
    if (d.ranges) { out[day] = d; continue; }
    // Old format: { enabled, start, end }
    out[day] = { enabled: d.enabled || false, ranges: [{ start: d.start||'09:00', end: d.end||'17:00' }] };
  }
  return out;
};

// Today midnight for filtering
const todayMidnight = () => new Date(new Date().setHours(0,0,0,0));

export default function Calendar() {
  const [activeTab,    setActiveTab]    = useState('pages');
  const [pages,        setPages]        = useState([]);
  const [bookings,     setBookings]     = useState([]);
  const [teamMembers,  setTeamMembers]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showModal,    setShowModal]    = useState(false);
  const [editPageId,   setEditPageId]   = useState(null);
  const [formData,     setFormData]     = useState({...BLANK_PAGE});
  const [selBooking,   setSelBooking]   = useState(null);
  const [deleting,     setDeleting]     = useState(false);
  const [copied,       setCopied]       = useState('');
  const [newQuestion,  setNewQuestion]  = useState('');
  const [newFormField, setNewFormField] = useState({label:'',type:'text',required:false,options:[]});
  const [newFFOption,  setNewFFOption]  = useState('');
  const [showFFForm,   setShowFFForm]   = useState(false);
  const [notification, notify]          = useNotify();

  useEffect(()=>{ fetchAll(); },[activeTab]);// eslint-disable-line

  const fetchAll = async () => {
    try {
      const [p,b,t] = await Promise.all([
        client.get('/api/calendar/pages'),
        client.get('/api/calendar/bookings'),
        client.get('/api/calendar/team-members').catch(()=>({data:[]})),
      ]);
      setPages(p.data||[]);
      setBookings(b.data||[]);
      setTeamMembers(t.data||[]);
    } catch { notify('Failed to load','error'); }
    finally { setLoading(false); }
  };

  const autoSlug = t => t.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'');

  const openCreate = () => {
    setFormData({...BLANK_PAGE, availability: makeDefaultAvail()});
    setEditPageId(null);
    setShowModal(true);
  };

  const openEdit = (page) => {
    setFormData({
      title:          page.title          || '',
      slug:           page.slug           || '',
      description:    page.description    || '',
      duration_mins:  page.duration_mins  || 30,
      booking_type:   page.booking_type   || 'one_on_one',
      location:       page.location       || '',
      color:          page.color          || '#0099FF',
      logo_url:       page.logo_url       || '',
      host_photo_url: page.host_photo_url || '',
      availability:   migrateAvail(page.availability),
      questions:      page.questions      || [],
      form_fields:    page.form_fields    || [],
      member_ids:     page.members?.map(m=>m.user?.id||m.user_id) || [],
      host_timezone:  page.host_timezone  || 'Asia/Kolkata',
    });
    setEditPageId(page.id);
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditPageId(null); };

  const handleSave = async () => {
    if (!formData.title||!formData.slug) return notify('Title and slug required','error');
    if (!formData.member_ids?.length)    return notify('Please assign at least one team member','error');
    try {
      if (editPageId) {
        await client.patch(`/api/calendar/pages/${editPageId}`, formData);
        notify('Booking page updated!');
      } else {
        await client.post('/api/calendar/pages', formData);
        notify('Booking page created!');
      }
      closeModal(); fetchAll();
    } catch (err) { notify(err.response?.data?.error||'Failed','error'); }
  };

  const handleDeletePage = async (id) => {
    if (!window.confirm('Delete this booking page? All associated bookings will also be deleted.')) return;
    try { await client.delete(`/api/calendar/pages/${id}`); setPages(pages.filter(p=>p.id!==id)); notify('Deleted'); }
    catch { notify('Failed to delete','error'); }
  };

  const handleToggle = async (page) => {
    try {
      await client.patch(`/api/calendar/pages/${page.id}`,{is_active:!page.is_active});
      setPages(pages.map(p=>p.id===page.id?{...p,is_active:!p.is_active}:p));
      notify(page.is_active?'Page deactivated':'Page activated');
    } catch { notify('Failed','error'); }
  };

  const handleDeleteBooking = async () => {
    if (!selBooking) return;
    if (!window.confirm(`Cancel booking for ${selBooking.booker_name}? They will receive a cancellation email.`)) return;
    setDeleting(true);
    try {
      await client.delete(`/api/calendar/bookings/${selBooking.id}`);
      setBookings(bookings.filter(b=>b.id!==selBooking.id));
      setSelBooking(null);
      notify(`Booking cancelled. ${selBooking.booker_name} and the host have been notified.`);
    } catch (err) { notify(err.response?.data?.error||'Failed to cancel','error'); }
    finally { setDeleting(false); }
  };

  const copyLink    = (slug) => { navigator.clipboard.writeText(`https://app.digitalxtension.com/book/${slug}`); setCopied(slug); setTimeout(()=>setCopied(''),2000); notify('Link copied!'); };
  const copyEmbed   = (slug) => { navigator.clipboard.writeText(`<iframe src="https://app.digitalxtension.com/book/${slug}" width="100%" height="700" frameborder="0" style="border-radius:12px;"></iframe>`); notify('Embed code copied!'); };
  const openPreview = (slug) => window.open(`https://app.digitalxtension.com/book/${slug}`,'_blank');

  // ── Availability helpers ─────────────────────────────────
  const setDayEnabled = (day, enabled) =>
    setFormData(f=>({...f, availability:{...f.availability,[day]:{...f.availability[day],enabled}}}));

  const setRange = (day, idx, field, val) =>
    setFormData(f => {
      const ranges = [...(f.availability[day]?.ranges||[])];
      ranges[idx] = {...ranges[idx],[field]:val};
      return {...f, availability:{...f.availability,[day]:{...f.availability[day],ranges}}};
    });

  const addRange = (day) =>
    setFormData(f => {
      const ranges = [...(f.availability[day]?.ranges||[]), {start:'09:00',end:'17:00'}];
      return {...f, availability:{...f.availability,[day]:{...f.availability[day],ranges}}};
    });

  const removeRange = (day, idx) =>
    setFormData(f => {
      const ranges = (f.availability[day]?.ranges||[]).filter((_,i)=>i!==idx);
      return {...f, availability:{...f.availability,[day]:{...f.availability[day],ranges:ranges.length?ranges:[{start:'09:00',end:'17:00'}]}}};
    });

  // ── Form field helpers ───────────────────────────────────
  const addFormField = () => {
    if (!newFormField.label.trim()) return;
    setFormData({...formData,form_fields:[...formData.form_fields,{...newFormField,id:Date.now()}]});
    setNewFormField({label:'',type:'text',required:false,options:[]});
    setNewFFOption(''); setShowFFForm(false);
  };
  const removeFormField = (idx) => setFormData({...formData,form_fields:formData.form_fields.filter((_,i)=>i!==idx)});
  const addFFOption     = () => { if(!newFFOption.trim()) return; setNewFormField({...newFormField,options:[...newFormField.options,newFFOption.trim()]}); setNewFFOption(''); };
  const needsOptions    = ['select','radio','checkbox','multiselect'].includes(newFormField.type);
  const addQuestion     = () => { if(!newQuestion.trim()) return; setFormData({...formData,questions:[...formData.questions,{label:newQuestion,required:false}]}); setNewQuestion(''); };

  const fmtBookingTime = (b) => {
    const tz = b.page?.host_timezone || 'Asia/Kolkata';
    return new Date(b.start_time).toLocaleString('en-IN',{timeZone:tz,dateStyle:'medium',timeStyle:'short'});
  };

  // Upcoming count for page card: only future confirmed bookings
  const upcomingCount = (pageId) =>
    bookings.filter(b => b.booking_page_id===pageId && b.status==='confirmed' && new Date(b.start_time)>=todayMidnight()).length;

  const TABS = [
    { id:'pages',    label:'Booking Pages', icon:'ti-calendar-plus'  },
    { id:'bookings', label:'Bookings',      icon:'ti-calendar-event' },
    { id:'avail',    label:'Availability',  icon:'ti-clock'          },
  ];

  if (loading) return <Spinner />;

  return (
    <div>
      <Toast {...notification} />

      {/* ══════════════════════════════════════════════════════
          MODAL — Create / Edit Booking Page
      ══════════════════════════════════════════════════════ */}
      {showModal && (
        <div
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:2000,display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'24px 16px',overflowY:'auto'}}
          onClick={closeModal}
        >
          <div
            style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:740,boxShadow:'0 25px 60px rgba(0,0,0,0.25)',marginBottom:24}}
            onClick={e=>e.stopPropagation()}
          >
            {/* Modal header */}
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'20px 28px',borderBottom:`1px solid ${C.border}`}}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <i className="ti ti-calendar-plus" style={{fontSize:20,color:C.primary}} />
                <span style={{fontWeight:700,fontSize:16,color:C.dark}}>
                  {editPageId ? 'Edit Booking Page' : 'New Booking Page'}
                </span>
              </div>
              <button onClick={closeModal} style={{background:'#f3f4f6',border:'none',borderRadius:8,padding:'6px 10px',cursor:'pointer',color:C.muted,fontSize:18,lineHeight:1}}>
                <i className="ti ti-x" />
              </button>
            </div>

            {/* Modal body */}
            <div style={{padding:'24px 28px',display:'flex',flexDirection:'column',gap:20}}>

              {/* Basic info */}
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C.muted,letterSpacing:0.5,marginBottom:12}}>BASIC INFO</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                  <Input label="Page Title *" placeholder="e.g. 30 Min Discovery Call"
                    value={formData.title} onChange={e=>setFormData({...formData,title:e.target.value,...(!editPageId&&{slug:autoSlug(e.target.value)})})} />
                  <Input label="URL Slug *" placeholder="e.g. discovery-call"
                    value={formData.slug} onChange={e=>setFormData({...formData,slug:e.target.value})} />
                  <div style={{gridColumn:'1/-1'}}>
                    <Input label="Description" placeholder="Brief description for bookers..."
                      value={formData.description} onChange={e=>setFormData({...formData,description:e.target.value})} />
                  </div>
                  <Input label="Location / Meeting Link" placeholder="https://meet.google.com/..."
                    value={formData.location} onChange={e=>setFormData({...formData,location:e.target.value})} />
                  <div>
                    <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:4,display:'block'}}>Duration</label>
                    <select style={{width:'100%',padding:'8px 12px',borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,outline:'none'}}
                      value={formData.duration_mins} onChange={e=>setFormData({...formData,duration_mins:parseInt(e.target.value)})}>
                      {DURATIONS.map(d=><option key={d} value={d}>{d} minutes</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Host Timezone */}
              <div>
                <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:4,display:'block'}}>
                  Your Timezone (Host) <span style={{fontSize:11,fontWeight:400,color:C.faint,marginLeft:4}}>Availability slots are in this timezone</span>
                </label>
                <select style={{width:'100%',padding:'8px 12px',borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,outline:'none'}}
                  value={formData.host_timezone} onChange={e=>setFormData({...formData,host_timezone:e.target.value})}>
                  {TIMEZONES.map(tz=>(
                    <option key={tz} value={tz}>
                      {tz.replace(/_/g,' ')} — {new Date().toLocaleTimeString('en',{timeZone:tz,hour:'2-digit',minute:'2-digit',timeZoneName:'short'})}
                    </option>
                  ))}
                </select>
              </div>

              {/* Branding */}
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C.muted,letterSpacing:0.5,marginBottom:12}}>BRANDING</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
                  <div>
                    <Input label="Page Logo URL" placeholder="https://..." value={formData.logo_url} onChange={e=>setFormData({...formData,logo_url:e.target.value})} />
                    <div style={{fontSize:11,color:C.faint,marginTop:4}}>Shown at top of booking page.</div>
                  </div>
                  <div>
                    <Input label="Host Photo URL" placeholder="https://..." value={formData.host_photo_url} onChange={e=>setFormData({...formData,host_photo_url:e.target.value})} />
                    <div style={{display:'flex',alignItems:'center',gap:8,marginTop:4}}>
                      <div style={{fontSize:11,color:C.faint}}>Circular photo in sidebar.</div>
                      {formData.host_photo_url && <img src={formData.host_photo_url} alt="preview" style={{width:28,height:28,borderRadius:'50%',objectFit:'cover',border:`2px solid ${formData.color||C.primary}`,flexShrink:0}} />}
                    </div>
                  </div>
                  <div>
                    <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:6,display:'block'}}>Page Color</label>
                    <div style={{display:'flex',gap:10,alignItems:'center'}}>
                      <input type="color" value={formData.color} onChange={e=>setFormData({...formData,color:e.target.value})}
                        style={{width:44,height:36,borderRadius:8,border:`1px solid ${C.border}`,cursor:'pointer',padding:2}} />
                      <input style={{flex:1,padding:'8px 12px',borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,outline:'none'}}
                        value={formData.color} onChange={e=>setFormData({...formData,color:e.target.value})} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Booking type */}
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C.muted,letterSpacing:0.5,marginBottom:12}}>BOOKING TYPE</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
                  {BOOKING_TYPES.map(t=>(
                    <button key={t.id} onClick={()=>setFormData({...formData,booking_type:t.id})}
                      style={{padding:12,borderRadius:10,border:`1px solid ${formData.booking_type===t.id?C.primary:C.border}`,background:formData.booking_type===t.id?'var(--color-primary-light)':'#fff',cursor:'pointer',textAlign:'left'}}>
                      <i className={`ti ${t.icon}`} style={{fontSize:20,color:formData.booking_type===t.id?C.primary:C.muted,display:'block',marginBottom:6}} />
                      <div style={{fontWeight:600,fontSize:13,color:formData.booking_type===t.id?C.primary:C.dark}}>{t.label}</div>
                      <div style={{fontSize:11,color:C.faint}}>{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Team members */}
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C.muted,letterSpacing:0.5,marginBottom:8}}>TEAM MEMBERS <span style={{color:C.danger}}>*</span></div>
                {!formData.member_ids?.length && <div style={{fontSize:11,color:C.danger,marginBottom:6}}>At least one member is required</div>}
                <div style={{display:'grid',gap:6,border:`1px solid ${!formData.member_ids?.length?C.danger:C.border}`,borderRadius:10,overflow:'hidden'}}>
                  {teamMembers.map((m,i)=>{
                    const checked = (formData.member_ids||[]).includes(m.id);
                    return (
                      <label key={m.id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:checked?'var(--color-primary-light)':'#fff',cursor:'pointer',borderBottom:i<teamMembers.length-1?`1px solid ${C.border}`:'none'}}>
                        <input type="checkbox" checked={checked} onChange={e=>{
                          const ids = formData.member_ids||[];
                          setFormData({...formData,member_ids:e.target.checked?[...ids,m.id]:ids.filter(x=>x!==m.id)});
                        }} style={{accentColor:C.primary}} />
                        <div style={{width:32,height:32,borderRadius:'50%',background:C.primary,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11,fontWeight:700,flexShrink:0}}>
                          {initials(m.name)}
                        </div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:500,color:C.dark}}>{m.name}</div>
                          <div style={{fontSize:11,color:C.muted}}>{m.email} · {m.role}</div>
                        </div>
                        {checked && <i className="ti ti-circle-check" style={{fontSize:18,color:C.primary}} />}
                      </label>
                    );
                  })}
                  {!teamMembers.length && <div style={{padding:16,textAlign:'center',fontSize:13,color:C.faint}}>No team members found. Add users in the Team section first.</div>}
                </div>
              </div>

              {/* Availability — multi-range per day */}
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C.muted,letterSpacing:0.5,marginBottom:4}}>WEEKLY AVAILABILITY</div>
                <div style={{fontSize:11,color:C.faint,marginBottom:12}}>in {formData.host_timezone?.replace(/_/g,' ')} · You can add multiple time ranges per day</div>
                <div style={{display:'grid',gap:8}}>
                  {DAYS.map(day => {
                    const dayData = formData.availability[day] || { enabled:false, ranges:[{start:'09:00',end:'17:00'}] };
                    return (
                      <div key={day} style={{background:'#f9fafb',borderRadius:10,border:`1px solid ${C.border}`,padding:'10px 14px'}}>
                        {/* Day header row */}
                        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:dayData.enabled?10:0}}>
                          <input type="checkbox" checked={dayData.enabled||false}
                            onChange={e=>setDayEnabled(day,e.target.checked)}
                            style={{cursor:'pointer',accentColor:C.primary,width:15,height:15}} />
                          <span style={{fontSize:13,fontWeight:600,width:100,color:dayData.enabled?C.dark:C.faint}}>{day}</span>
                          {!dayData.enabled && <span style={{fontSize:11,color:C.faint}}>Not available</span>}
                          {dayData.enabled && (
                            <button onClick={()=>addRange(day)}
                              style={{marginLeft:'auto',background:'transparent',border:`1px dashed ${C.primary}`,borderRadius:6,padding:'3px 10px',cursor:'pointer',fontSize:11,color:C.primary,display:'flex',alignItems:'center',gap:4}}>
                              <i className="ti ti-plus" style={{fontSize:11}} /> Add slot
                            </button>
                          )}
                        </div>
                        {/* Time ranges */}
                        {dayData.enabled && (dayData.ranges||[]).map((range, ridx) => (
                          <div key={ridx} style={{display:'flex',alignItems:'center',gap:8,marginBottom:ridx<dayData.ranges.length-1?8:0,paddingLeft:27}}>
                            <input type="time" value={range.start}
                              onChange={e=>setRange(day,ridx,'start',e.target.value)}
                              style={{padding:'5px 8px',borderRadius:6,border:`1px solid ${C.border}`,fontSize:12,outline:'none',background:'#fff'}} />
                            <span style={{color:C.faint,fontSize:12}}>to</span>
                            <input type="time" value={range.end}
                              onChange={e=>setRange(day,ridx,'end',e.target.value)}
                              style={{padding:'5px 8px',borderRadius:6,border:`1px solid ${C.border}`,fontSize:12,outline:'none',background:'#fff'}} />
                            {dayData.ranges.length > 1 && (
                              <button onClick={()=>removeRange(day,ridx)}
                                style={{background:'#FEF2F2',border:'none',borderRadius:6,padding:'4px 8px',cursor:'pointer',color:C.danger,fontSize:12,lineHeight:1}}>
                                <i className="ti ti-x" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Custom form fields */}
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C.muted,letterSpacing:0.5,marginBottom:8}}>
                  CUSTOM FORM FIELDS
                  <span style={{fontSize:11,fontWeight:400,color:C.faint,marginLeft:8,textTransform:'none'}}>Added to the booking form</span>
                </div>
                {formData.form_fields.map((f,i)=>(
                  <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:'#f9fafb',borderRadius:8,marginBottom:6,border:`1px solid ${C.border}`}}>
                    <i className={`ti ${FORM_FIELD_TYPES.find(t=>t.id===f.type)?.icon||'ti-forms'}`} style={{fontSize:16,color:C.primary}} />
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:500}}>{f.label}</div>
                      <div style={{fontSize:11,color:C.faint}}>{f.type}{f.required?' · Required':''}{f.options?.length>0?` · ${f.options.join(', ')}`:''}</div>
                    </div>
                    <button onClick={()=>removeFormField(i)} style={{background:'#FEF2F2',border:'none',borderRadius:6,padding:'4px 8px',cursor:'pointer',color:C.danger,fontSize:12}}>
                      <i className="ti ti-trash" />
                    </button>
                  </div>
                ))}
                {showFFForm ? (
                  <div style={{background:'#f9fafb',borderRadius:10,border:`1px solid ${C.border}`,padding:14,marginTop:8}}>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                      <Input label="Field Label" placeholder="e.g. Company Name" value={newFormField.label} onChange={e=>setNewFormField({...newFormField,label:e.target.value})} />
                      <div>
                        <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:4,display:'block'}}>Type</label>
                        <select style={{width:'100%',padding:'8px 12px',borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,outline:'none'}}
                          value={newFormField.type} onChange={e=>setNewFormField({...newFormField,type:e.target.value,options:[]})}>
                          {FORM_FIELD_TYPES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:needsOptions?10:0}}>
                      <input type="checkbox" checked={newFormField.required} onChange={e=>setNewFormField({...newFormField,required:e.target.checked})} style={{accentColor:C.primary}} />
                      <span style={{fontSize:13,color:C.text}}>Required field</span>
                    </div>
                    {needsOptions && (
                      <div style={{marginBottom:10}}>
                        <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:6,display:'block'}}>Options</label>
                        <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:6}}>
                          {newFormField.options.map((opt,i)=>(
                            <span key={i} style={{background:'var(--color-primary-light)',color:C.primary,borderRadius:20,padding:'3px 10px',fontSize:12,display:'flex',alignItems:'center',gap:4}}>
                              {opt}<button onClick={()=>setNewFormField({...newFormField,options:newFormField.options.filter((_,idx)=>idx!==i)})} style={{background:'none',border:'none',cursor:'pointer',color:C.faint,padding:0,lineHeight:1}}>×</button>
                            </span>
                          ))}
                        </div>
                        <div style={{display:'flex',gap:8}}>
                          <input style={{flex:1,padding:'7px 12px',borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,outline:'none'}}
                            placeholder="Type option and press Enter..." value={newFFOption} onChange={e=>setNewFFOption(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addFFOption()} />
                          <Btn variant="outline" size="sm" onClick={addFFOption}><i className="ti ti-plus" /> Add</Btn>
                        </div>
                      </div>
                    )}
                    <div style={{display:'flex',gap:8}}>
                      <Btn size="sm" onClick={addFormField}><i className="ti ti-check" /> Add Field</Btn>
                      <Btn size="sm" variant="outline" onClick={()=>setShowFFForm(false)}>Cancel</Btn>
                    </div>
                  </div>
                ) : (
                  <Btn variant="outline" onClick={()=>setShowFFForm(true)} style={{marginTop:8}}>
                    <i className="ti ti-plus" /> Add Form Field
                  </Btn>
                )}
              </div>

              {/* Simple questions */}
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C.muted,letterSpacing:0.5,marginBottom:8}}>
                  SIMPLE QUESTIONS
                  <span style={{fontSize:11,fontWeight:400,color:C.faint,marginLeft:8,textTransform:'none'}}>Text-only</span>
                </div>
                {formData.questions.map((q,i)=>(
                  <div key={i} style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                    <span style={{flex:1,fontSize:13,padding:'6px 12px',background:'#f9fafb',borderRadius:8,border:`1px solid ${C.border}`}}>{q.label}</span>
                    <button onClick={()=>setFormData({...formData,questions:formData.questions.filter((_,idx)=>idx!==i)})}
                      style={{background:'#FEF2F2',border:'none',borderRadius:6,padding:'5px 8px',cursor:'pointer',color:C.danger,fontSize:12}}>
                      <i className="ti ti-trash" />
                    </button>
                  </div>
                ))}
                <div style={{display:'flex',gap:8}}>
                  <input style={{flex:1,padding:'8px 12px',borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,outline:'none'}}
                    placeholder="e.g. What's your main goal?" value={newQuestion} onChange={e=>setNewQuestion(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addQuestion()} />
                  <Btn variant="outline" onClick={addQuestion}><i className="ti ti-plus" /> Add</Btn>
                </div>
              </div>

            </div>

            {/* Modal footer */}
            <div style={{display:'flex',gap:8,padding:'16px 28px',borderTop:`1px solid ${C.border}`,background:'#f9fafb',borderRadius:'0 0 16px 16px'}}>
              <Btn onClick={handleSave}><i className="ti ti-check" /> {editPageId?'Update Page':'Create Page'}</Btn>
              <Btn variant="outline" onClick={closeModal}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          BOOKING DETAIL POPUP
      ══════════════════════════════════════════════════════ */}
      {selBooking && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={()=>setSelBooking(null)}>
          <div style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:520,maxHeight:'90vh',overflow:'auto',boxShadow:'0 25px 50px rgba(0,0,0,0.3)'}} onClick={e=>e.stopPropagation()}>
            <div style={{background:selBooking.page?.color||C.primary,padding:'20px 24px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div>
                <div style={{fontWeight:700,fontSize:16,color:'#fff'}}>{selBooking.page?.title||'Booking'}</div>
                <div style={{fontSize:12,color:'rgba(255,255,255,0.75)',marginTop:2}}>
                  {fmtBookingTime(selBooking)}
                  <span style={{marginLeft:6,opacity:0.7}}>({selBooking.page?.host_timezone||'Asia/Kolkata'})</span>
                </div>
              </div>
              <button onClick={()=>setSelBooking(null)} style={{background:'rgba(255,255,255,0.2)',border:'none',borderRadius:8,padding:'6px 10px',cursor:'pointer',color:'#fff',fontSize:18}}>
                <i className="ti ti-x" />
              </button>
            </div>
            <div style={{padding:24}}>
              <div style={{marginBottom:16}}>
                <span style={{background:selBooking.status==='confirmed'?'#f0fdf4':selBooking.status==='cancelled'?'#fef2f2':'#f9fafb',color:selBooking.status==='confirmed'?C.success:selBooking.status==='cancelled'?C.danger:C.muted,borderRadius:20,padding:'4px 12px',fontSize:12,fontWeight:600}}>
                  {selBooking.status?.toUpperCase()}
                </span>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:16}}>
                {[['Name',selBooking.booker_name],['Email',selBooking.booker_email],['Phone',selBooking.booker_phone||'—'],['Host',selBooking.host?.name||'—']].map(([k,v])=>(
                  <div key={k}>
                    <div style={{fontSize:11,color:C.faint,marginBottom:2}}>{k}</div>
                    <div style={{fontSize:13,fontWeight:500,color:C.dark}}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{background:'#f9fafb',borderRadius:10,border:`1px solid ${C.border}`,padding:'12px 16px',marginBottom:16}}>
                <div style={{fontSize:11,color:C.faint,marginBottom:6}}>DATE & TIME</div>
                <div style={{fontSize:14,fontWeight:600,color:C.dark,marginBottom:4}}>
                  {new Date(selBooking.start_time).toLocaleDateString('en-IN',{timeZone:selBooking.page?.host_timezone||'Asia/Kolkata',weekday:'long',day:'numeric',month:'long',year:'numeric'})}
                </div>
                <div style={{fontSize:13,color:selBooking.page?.color||C.primary,fontWeight:500}}>
                  {new Date(selBooking.start_time).toLocaleTimeString('en-IN',{timeZone:selBooking.page?.host_timezone||'Asia/Kolkata',timeStyle:'short'})}
                  {' — '}
                  {new Date(selBooking.end_time).toLocaleTimeString('en-IN',{timeZone:selBooking.page?.host_timezone||'Asia/Kolkata',timeStyle:'short'})}
                  <span style={{fontSize:11,color:C.faint,marginLeft:6}}>({selBooking.page?.host_timezone||'Asia/Kolkata'})</span>
                </div>
                {selBooking.booker_timezone && selBooking.booker_timezone!==(selBooking.page?.host_timezone||'Asia/Kolkata') && (
                  <div style={{fontSize:12,color:C.muted,marginTop:6,paddingTop:6,borderTop:`1px solid ${C.border}`}}>
                    Booker's time: {new Date(selBooking.start_time).toLocaleTimeString('en-IN',{timeZone:selBooking.booker_timezone,timeStyle:'short'})} ({selBooking.booker_timezone})
                  </div>
                )}
              </div>
              {selBooking.page?.location && (
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:11,color:C.faint,marginBottom:4}}>LOCATION</div>
                  <div style={{fontSize:13,color:C.dark}}>{selBooking.page.location}</div>
                </div>
              )}
              {selBooking.answers && Object.keys(selBooking.answers).length>0 && (
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:10}}>Form Responses</div>
                  {Object.entries(selBooking.answers).map(([k,v])=>(
                    <div key={k} style={{marginBottom:8}}>
                      <div style={{fontSize:11,color:C.faint,marginBottom:2}}>{k}</div>
                      <div style={{fontSize:13,color:C.dark}}>{Array.isArray(v)?v.join(', '):v||'—'}</div>
                    </div>
                  ))}
                </div>
              )}
              {selBooking.notes && (
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:11,color:C.faint,marginBottom:4}}>NOTES</div>
                  <div style={{fontSize:13,color:C.dark,background:'#f9fafb',borderRadius:8,padding:'10px 12px'}}>{selBooking.notes}</div>
                </div>
              )}
              <div style={{display:'flex',gap:8,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
                {selBooking.status==='confirmed' && (
                  <Btn variant="danger" onClick={handleDeleteBooking} disabled={deleting}>
                    <i className="ti ti-calendar-x" />{deleting?'Cancelling...':'Cancel Booking'}
                  </Btn>
                )}
                <Btn variant="outline" onClick={()=>setSelBooking(null)}>Close</Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <div>
          <h1 style={{fontSize:20,fontWeight:700,color:C.dark,marginBottom:2}}>Calendar & Booking</h1>
          <p style={{fontSize:13,color:C.muted}}>Create public booking pages for your leads and clients</p>
        </div>
        <Btn onClick={openCreate}><i className="ti ti-plus" /> New Booking Page</Btn>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:4,marginBottom:24,background:'#fff',borderRadius:12,border:`1px solid ${C.border}`,padding:6}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setActiveTab(t.id)}
            style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'9px 12px',borderRadius:8,border:'none',background:activeTab===t.id?C.primary:'transparent',color:activeTab===t.id?'#fff':C.muted,fontSize:12,fontWeight:activeTab===t.id?600:400,cursor:'pointer'}}>
            <i className={`ti ${t.icon}`} style={{fontSize:15}} />{t.label}
          </button>
        ))}
      </div>

      {/* Booking Pages tab */}
      {activeTab==='pages' && (
        pages.length===0 ? (
          <div style={{background:'#fff',borderRadius:12,border:`2px dashed ${C.border}`,padding:48,textAlign:'center'}}>
            <i className="ti ti-calendar-plus" style={{fontSize:40,color:'#d1d5db',display:'block',marginBottom:12}} />
            <div style={{fontWeight:600,fontSize:15,color:C.dark,marginBottom:6}}>No booking pages yet</div>
            <div style={{fontSize:13,color:C.faint,marginBottom:20}}>Create your first booking page to share with leads and clients</div>
            <Btn onClick={openCreate}><i className="ti ti-plus" /> Create Booking Page</Btn>
          </div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:16}}>
            {pages.map(page=>(
              <div key={page.id} style={{background:'#fff',borderRadius:12,border:`1px solid ${C.border}`,overflow:'hidden'}}>
                <div style={{height:5,background:page.color||C.primary}} />
                <div style={{padding:20}}>
                  <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:10}}>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      {page.host_photo_url && <img src={page.host_photo_url} alt="host" style={{width:36,height:36,borderRadius:'50%',objectFit:'cover',border:`2px solid ${page.color||C.primary}`,flexShrink:0}} />}
                      {!page.host_photo_url && page.logo_url && <img src={page.logo_url} alt="logo" style={{height:28,objectFit:'contain',borderRadius:4,flexShrink:0}} />}
                      <div>
                        <div style={{fontWeight:700,fontSize:15,color:C.dark,marginBottom:2}}>{page.title}</div>
                        <div style={{fontSize:12,color:C.muted}}>{page.description}</div>
                      </div>
                    </div>
                    <button onClick={()=>handleToggle(page)}
                      style={{padding:'4px 10px',borderRadius:20,border:'none',background:page.is_active?'#f0fdf4':'#f3f4f6',color:page.is_active?C.success:C.faint,fontSize:11,cursor:'pointer',fontWeight:500,whiteSpace:'nowrap'}}>
                      {page.is_active?'● Live':'○ Off'}
                    </button>
                  </div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12}}>
                    <span style={{background:'#f3f4f6',borderRadius:20,padding:'3px 10px',fontSize:11,color:C.text}}>
                      <i className="ti ti-clock" style={{fontSize:11,marginRight:4}} />{page.duration_mins} min
                    </span>
                    <span style={{background:'#f3f4f6',borderRadius:20,padding:'3px 10px',fontSize:11,color:C.text}}>
                      {page.booking_type?.replace('_',' ')}
                    </span>
                    <span style={{background:'#f3f4f6',borderRadius:20,padding:'3px 10px',fontSize:11,color:C.text}}>
                      <i className="ti ti-world" style={{fontSize:11,marginRight:4}} />{page.host_timezone?.split('/').pop()?.replace(/_/g,' ')||'IST'}
                    </span>
                    {page.members?.length>0 && (
                      <span style={{background:'var(--color-primary-light)',borderRadius:20,padding:'3px 10px',fontSize:11,color:C.primary}}>
                        <i className="ti ti-users" style={{fontSize:11,marginRight:4}} />{page.members.length} member{page.members.length>1?'s':''}
                      </span>
                    )}
                    <span style={{background:'#f3f4f6',borderRadius:20,padding:'3px 10px',fontSize:11,color:C.text}}>
                      {upcomingCount(page.id)} upcoming
                    </span>
                  </div>
                  <div style={{background:'#f9fafb',borderRadius:8,padding:'8px 12px',display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
                    <i className="ti ti-link" style={{fontSize:14,color:C.faint,flexShrink:0}} />
                    <span style={{fontSize:11,color:C.muted,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      app.digitalxtension.com/book/{page.slug}
                    </span>
                    <button onClick={()=>copyLink(page.slug)}
                      style={{padding:'3px 10px',borderRadius:6,border:`1px solid ${C.primary}`,background:'transparent',color:C.primary,fontSize:11,cursor:'pointer',flexShrink:0}}>
                      {copied===page.slug?'✓ Copied':'Copy'}
                    </button>
                  </div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                    <Btn size="sm" onClick={()=>openPreview(page.slug)}><i className="ti ti-external-link" /> Preview</Btn>
                    <Btn size="sm" variant="ghost" onClick={()=>openEdit(page)}><i className="ti ti-edit" /> Edit</Btn>
                    <Btn size="sm" variant="ghost" onClick={()=>copyEmbed(page.slug)}><i className="ti ti-code" /> Embed</Btn>
                    <button onClick={()=>handleDeletePage(page.id)}
                      style={{padding:'5px 10px',borderRadius:6,border:'none',background:'#FEF2F2',color:C.danger,cursor:'pointer',fontSize:12}}>
                      <i className="ti ti-trash" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Bookings tab */}
      {activeTab==='bookings' && (
        <Card title="Upcoming Bookings" icon="ti-calendar-event"
          extra={<span style={{fontSize:12,color:C.muted}}>Click any booking to view details</span>}>
          {bookings.filter(b=>b.status==='confirmed'&&new Date(b.start_time)>=todayMidnight()).length===0 ? (
            <div style={{textAlign:'center',padding:30,color:C.faint,fontSize:13}}>
              <i className="ti ti-calendar-off" style={{fontSize:32,display:'block',marginBottom:8}} />
              No upcoming bookings.
            </div>
          ) : (
            bookings.filter(b=>b.status==='confirmed'&&new Date(b.start_time)>=todayMidnight()).map((b,i,arr)=>(
              <div key={b.id} onClick={()=>setSelBooking(b)}
                style={{display:'flex',alignItems:'center',gap:14,padding:'12px 0',borderBottom:i<arr.length-1?`1px solid #f3f4f6`:'none',cursor:'pointer',borderRadius:8,transition:'background 0.1s'}}
                onMouseEnter={e=>e.currentTarget.style.background='#f9fafb'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div style={{width:40,height:40,borderRadius:10,background:`${b.page?.color||C.primary}18`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <i className="ti ti-calendar-event" style={{fontSize:18,color:b.page?.color||C.primary}} />
                </div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:500,fontSize:13}}>{b.booker_name}</div>
                  <div style={{fontSize:11,color:C.muted}}>
                    {b.booker_email} · {fmtBookingTime(b)}
                    {b.page&&<span> · {b.page.title}</span>}
                    {b.host&&<span style={{color:b.page?.color||C.primary}}> → {b.host.name}</span>}
                  </div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{background:'#f0fdf4',color:C.success,borderRadius:20,padding:'3px 12px',fontSize:11,fontWeight:500}}>confirmed</span>
                  <i className="ti ti-chevron-right" style={{fontSize:14,color:C.faint}} />
                </div>
              </div>
            ))
          )}

          {/* Cancelled — only until appointment date */}
          {bookings.filter(b=>b.status==='cancelled'&&new Date(b.start_time)>=todayMidnight()).length>0 && (
            <div style={{marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
              <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:10}}>Cancelled</div>
              {bookings.filter(b=>b.status==='cancelled'&&new Date(b.start_time)>=todayMidnight()).map((b,i,arr)=>(
                <div key={b.id} onClick={()=>setSelBooking(b)}
                  style={{display:'flex',alignItems:'center',gap:14,padding:'10px 0',borderBottom:i<arr.length-1?`1px solid #f3f4f6`:'none',cursor:'pointer',opacity:0.65}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:500,fontSize:13}}>{b.booker_name}</div>
                    <div style={{fontSize:11,color:C.muted}}>{fmtBookingTime(b)}</div>
                  </div>
                  <span style={{background:'#fef2f2',color:C.danger,borderRadius:20,padding:'3px 12px',fontSize:11}}>cancelled</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Availability tab */}
      {activeTab==='avail' && (
        <Card title="Default Availability" icon="ti-clock">
          <p style={{fontSize:13,color:C.muted,marginBottom:16}}>Your default working hours applied to new booking pages.</p>
          <div style={{display:'grid',gap:8}}>
            {DAYS.map(day=>(
              <div key={day} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:'#f9fafb',borderRadius:8,border:`1px solid ${C.border}`}}>
                <input type="checkbox" defaultChecked={!['Saturday','Sunday'].includes(day)} style={{cursor:'pointer',accentColor:C.primary}} />
                <span style={{fontSize:13,fontWeight:500,width:100}}>{day}</span>
                <input type="time" defaultValue="09:00" style={{padding:'5px 10px',borderRadius:6,border:`1px solid ${C.border}`,fontSize:12,outline:'none'}} />
                <span style={{color:C.faint,fontSize:13}}>—</span>
                <input type="time" defaultValue="17:00" style={{padding:'5px 10px',borderRadius:6,border:`1px solid ${C.border}`,fontSize:12,outline:'none'}} />
              </div>
            ))}
          </div>
          <div style={{marginTop:16}}>
            <Btn onClick={()=>notify('Availability saved!')}><i className="ti ti-device-floppy" /> Save Availability</Btn>
          </div>
        </Card>
      )}
    </div>
  );
}
